'use client';

import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SAVINGS_GOAL, type SavingsSummary } from '@/src/shared/lib/chart';
import { savingsRateText } from '@/src/shared/lib/figureFormat';
import { AnimatedAmount } from './animated-amount';

// Past this the rate is written bounded, as on the bars.
const RATE_LIMIT = 999;

interface Props {
  summary: SavingsSummary;
  average: number | null;
  total: number;
  /** The months the average covers: those with income. */
  withIncome: number;
  locale: string;
  /** The latest month's name, lower case, to sit mid-sentence. */
  month: string;
  noIncome: string;
  formatMoney: (amount: number) => string;
  reduced: boolean;
}

/** The latest month's rate, what it meant in pesos, and how the period did against the goal. */
export function SavingsRateSummary(props: Props) {
  const { summary, average, total, withIncome, locale, month, noIncome, formatMoney, reduced } =
    props;
  const t = useTranslations('common.savingsChart');
  const pct = summary.latest.pct;
  const met = pct !== null && pct >= SAVINGS_GOAL;
  const kept = summary.saved >= 0;

  return (
    <motion.div
      className="flex flex-col gap-4 rounded-xl border border-border bg-[linear-gradient(160deg,hsl(var(--primary)/0.08),transparent_60%)] p-4"
      initial={reduced ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div>
        <p className="text-[12px] font-medium text-muted-foreground">{t('rateOf', { month })}</p>
        {pct === null || pct < -RATE_LIMIT ? (
          <span className="mt-1 block text-[40px] leading-none font-bold tracking-[-0.03em] text-foreground tabular-nums">
            {pct === null ? '–' : savingsRateText(pct, locale)}
          </span>
        ) : (
          <AnimatedAmount
            value={Math.abs(Math.round(pct))}
            enabled={!reduced}
            currency={null}
            locale={locale}
            prefix={pct < 0 ? '−' : ''}
            suffix="%"
            duration={1100}
            className="mt-1 block text-[40px] leading-none font-bold tracking-[-0.03em] text-foreground tabular-nums"
          />
        )}
        <p className="mt-1.5 text-[12px] text-muted-foreground">
          {pct === null
            ? noIncome
            : t(kept ? 'saved' : 'overspent', { amount: formatMoney(Math.abs(summary.saved)) })}
        </p>
      </div>
      {met && (
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground dark:bg-primary/10 dark:text-primary">
          <CheckCircle2 className="size-3.5" aria-hidden="true" />
          {t('goalMet')}
        </span>
      )}
      <dl className="grid grid-cols-2 gap-3 border-t border-border pt-3 text-[11px]">
        <div>
          <dt className="text-muted-foreground">{t('average', { count: withIncome })}</dt>
          <dd className="mt-0.5 text-[15px] font-semibold text-foreground tabular-nums">
            {average === null ? '–' : savingsRateText(average, locale)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('monthsOver')}</dt>
          <dd className="mt-0.5 text-[13px] font-semibold text-foreground tabular-nums">
            {t('monthsOverValue', { count: summary.monthsOverGoal, total })}
          </dd>
        </div>
      </dl>
    </motion.div>
  );
}
